import React, { useState, useEffect } from 'react';
import { Box, Toolbar, Container, useTheme, useMediaQuery, Drawer } from '@mui/material';
import { Outlet, useLocation } from 'react-router-dom';
import NotionHeader from './NotionHeader';
import AvisoDemo from './AvisoDemo';
import NotionSidebar from './NotionSidebar';
import OnboardingSidebar from './OnboardingSidebar';
import TrialBanner from './TrialBanner';
import TrialExpiredScreen from './TrialExpiredScreen';
import HelpAssistant from './HelpAssistant';
import apiClient from '../services/api';
import { OnboardingRefreshContext } from '../contexts/OnboardingRefreshContext';
import { useAuth } from '../contexts/AuthContext';
import { esAdmin } from '../utils/roles';
// 10 ago 2026 (Augusto: "adicionaste la opción para que el usuario pague
// de una vez? sale el mensaje que es un trial de 14 días..."): este aviso
// (prueba gratis + precio + "pagar ahora") vivía solo en WelcomePage.js
// (/welcome) -- pero el registro nuevo ahora pasa por /onboarding/intro
// -> /profile -> /accounts -> /debts sin pasar por /welcome, así que un
// cliente nuevo podía terminar todo el onboarding sin verlo nunca. Se
// monta acá porque NotionLayout envuelve TODAS las rutas del estilo
// Notion (/profile, /accounts, /debts, /welcome, /dashboard, etc.), así
// que aparece sin importar cuál sea la primera pantalla real que visite
// el cliente después del registro. El propio componente ya se encarga de
// no mostrarse dos veces (localStorage) ni a quien ya pagó/es admin.
import TrialOfferModal from './TrialOfferModal';

// 5 ago 2026 (Tarea #92): rutas donde el cliente debe poder entrar
// aunque la prueba gratuita haya vencido -- tiene que poder ver planes y
// pagar (/subscription), y seguir viendo/editando su perfil o borrando
// su cuenta (/profile). El backend ya deja estas mismas rutas sin
// bloquear (ver api/v1/api.py); esto es el espejo en el frontend para no
// mostrar la pantalla de bloqueo encima de la única página que sí le
// sirve a un cliente vencido.
const TRIAL_GATE_EXEMPT_PREFIXES = ['/subscription', '/profile'];

// Ancho del sidebar -- mismo valor fijo que ya usan NotionSidebar.js y
// OnboardingSidebar.js (SidebarWrapper), para que el Drawer de móvil de
// abajo no quede más angosto/ancho que la versión de escritorio.
const SIDEBAR_WIDTH = 220;

// 4 sep 2026 (Augusto: "tiene que haber una opción para esconder la
// barra del lado izquierdo"): la preferencia se guarda en localStorage
// para que sobreviva a una recarga real de página (F5, cerrar/abrir
// pestaña) o a cualquier navegación que sí siga recargando por fuera de
// React Router (login, checkout externo, etc.).
// 7 sep 2026 (Augusto: "la velocidad ha bajado mucho... demora al
// moverse de sección a sección", peor en celular): NotionSidebar.js
// (casi todos sus links) usaba `window.location.href` en vez de
// navigate() de React Router, forzando una recarga COMPLETA de la app en
// cada clic de navegación (rehace el bundle, remonta todo, repite
// fetches de /dashboard/accounts, /onboarding/data, /subscription/current,
// etc.). Se corrigió ese archivo para usar navegación de React Router en
// todos los links del menú -- este comentario queda como registro de por
// qué existía ese comportamiento antes.
const SIDEBAR_OPEN_STORAGE_KEY = 'pcm_sidebar_open';

const NotionLayout = ({ title, breadcrumbs, children }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  // En móvil siempre arranca cerrado (es un Drawer superpuesto). En
  // escritorio, respeta lo último que el cliente eligió; si nunca lo
  // tocó, arranca abierto (comportamiento de siempre).
  const [open, setOpen] = useState(() => {
    if (isMobile) return false;
    const saved = localStorage.getItem(SIDEBAR_OPEN_STORAGE_KEY);
    return saved !== null ? saved === 'true' : true;
  });
  const location = useLocation();

  // Mientras el cliente no termine Perfil + Cuentas + Deudas, se muestra
  // la barra de pasos (OnboardingSidebar) en vez de la barra completa con
  // Tablero/Calendario — no tiene sentido ofrecer esas vistas todavía si
  // no hay datos cargados. `null` = todavía no sabemos (cargando), y en
  // ese estado no se muestra ninguna de las dos para evitar el parpadeo
  // de "primero veo el tablero completo y después se reduce".
  const [onboarding, setOnboarding] = useState(null);
  const [onboardingLoaded, setOnboardingLoaded] = useState(false);
  // 10 ago 2026: contador que las páginas hijas (CuentasPage.js,
  // DebtsPage.js) pueden incrementar vía OnboardingRefreshContext para
  // forzar un nuevo pedido a /onboarding/data sin depender de un cambio
  // de ruta -- ver comentario completo en ese archivo.
  const [onboardingRefreshTick, setOnboardingRefreshTick] = useState(0);
  const triggerOnboardingRefresh = () => setOnboardingRefreshTick((t) => t + 1);

  // 5 ago 2026 (Tarea #92): estado de la prueba gratuita, para bloquear
  // el contenido de la página cuando venció sin pago. `null` = todavía
  // no se sabe (ni bloquea ni deja pasar mientras carga, para no mostrar
  // un parpadeo de contenido real seguido del bloqueo).
  const [trialStatus, setTrialStatus] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/subscription/current')
      .then((response) => {
        if (!cancelled) setTrialStatus(response.data);
      })
      .catch(() => {
        // Si falla la consulta, no bloqueamos por un error de red --
        // mismo criterio que TrialBanner.js.
        if (!cancelled) setTrialStatus({ trial_expirado: false });
      });
    return () => {
      cancelled = true;
    };
  }, [location.pathname]);

  const isExemptFromTrialGate = TRIAL_GATE_EXEMPT_PREFIXES.some((prefix) =>
    location.pathname.startsWith(prefix)
  );
  const isBlockedByTrial =
    !isExemptFromTrialGate && trialStatus?.trial_expirado && !trialStatus?.pago;

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/onboarding/data')
      .then((response) => {
        if (!cancelled) setOnboarding(response.data);
      })
      .catch(() => {
        // Si falla, no bloqueamos al usuario — asumimos onboarding
        // completo y mostramos la barra normal en vez de dejarlo varado.
        if (!cancelled) setOnboarding({ completed: true });
      })
      .finally(() => {
        if (!cancelled) setOnboardingLoaded(true);
      });
    return () => {
      cancelled = true;
    };
    // Se vuelve a pedir en cada cambio de ruta (no solo al montar). Antes
    // esto tenía `[]` como dependencia, así que quedaba pegado con el
    // estado que existía la primera vez que se montó NotionLayout: si el
    // cliente guardaba su Perfil y la app navegaba a /accounts SIN
    // recargar la página completa (navegación normal de React Router), el
    // checklist se quedaba mostrando "0/3" hasta que alguien apretaba F5,
    // aunque el backend ya tenía profile_completed=true. Con
    // `location.pathname` como dependencia, cada paso completado se
    // refleja apenas se navega al siguiente.
    //
    // 10 ago 2026: se suma `onboardingRefreshTick` -- cubre el caso en que
    // el cliente agrega una cuenta o deuda SIN cambiar de ruta (se queda
    // en /accounts o /debts agregando varias seguidas). Ver
    // OnboardingRefreshContext.js.
  }, [location.pathname, onboardingRefreshTick]);

  const toggleDrawer = () => {
    setOpen((prev) => {
      const next = !prev;
      // En escritorio (no Drawer, sidebar fijo) guardamos la preferencia
      // para que sobreviva la recarga completa de página que dispara casi
      // cualquier click de navegación del sidebar (window.location.href).
      // En móvil no hace falta -- ahí siempre arranca cerrado.
      if (!isMobile) localStorage.setItem(SIDEBAR_OPEN_STORAGE_KEY, String(next));
      return next;
    });
  };

  // 7 ago 2026 (Augusto: "al abrir en el celular se cortan accesos y
  // features, no está apto para uso en browser en teléfono"): el sidebar
  // se cierra solo cada vez que se navega a otra ruta -- si no, al elegir
  // una sección desde el Drawer de móvil, este se quedaba abierto tapando
  // la página recién cargada hasta que el cliente lo cerraba a mano.
  useEffect(() => {
    if (isMobile) setOpen(false);
  }, [location.pathname, isMobile]);

  // 17 sep 2026 (Augusto, con la cuenta demo: "no veo el menu ADMIN").
  // Un administrador siempre ve la barra completa, aunque su onboarding
  // esté a medias. Era un círculo vicioso: la barra de pasos reemplaza a
  // la barra entera, así que ADMIN desaparecía de TODA la app -- y el
  // botón que carga la data de demo (y con ella completa el onboarding)
  // vive justo adentro de ADMIN. No había forma de salir sin escribir la
  // URL a mano.
  //
  // Más allá de ese caso puntual: el onboarding existe para guiar a un
  // cliente nuevo, no para esconderle a Augusto el panel donde administra
  // su propio producto.
  const { user } = useAuth();
  const mostrarBarraCompleta = onboarding?.completed || esAdmin(user);

  const sidebarContent = onboardingLoaded && (
    mostrarBarraCompleta
      ? <NotionSidebar />
      : <OnboardingSidebar onboarding={onboarding} />
  );

  return (
    <OnboardingRefreshContext.Provider value={triggerOnboardingRefresh}>
    <TrialOfferModal />
    <Box sx={{ display: 'flex', height: '100vh', overflow: 'hidden' }}>
      {/* Barra lateral -- fija en escritorio (como antes), Drawer
          superpuesto en móvil. Antes solo se dibujaba con `!isMobile`,
          así que en el celular no había NINGUNA forma de navegar entre
          secciones: el estado `open`/toggleDrawer ya existía pero nunca
          se conectaba a un botón real (ver fix en NotionHeader.js).
          4 sep 2026 (Augusto: "tiene que haber una opción para esconder
          la barra del lado izquierdo"): en escritorio ahora también se
          respeta `open` -- si el cliente la escondió (botón del
          encabezado, ver NotionHeader.js), no se dibuja nada acá y el
          área principal ocupa todo el ancho. */}
      {isMobile ? (
        sidebarContent && (
          <Drawer
            variant="temporary"
            open={open}
            onClose={toggleDrawer}
            ModalProps={{ keepMounted: true }}
            sx={{
              '& .MuiDrawer-paper': {
                boxSizing: 'border-box',
                width: SIDEBAR_WIDTH,
                border: 'none',
              },
            }}
          >
            {sidebarContent}
          </Drawer>
        )
      ) : (
        open && sidebarContent
      )}

      {/* Área principal */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          backgroundColor: theme.palette.background.default,
        }}
      >
        {/* 8 oct 2026: franja de demostración. Va ARRIBA del encabezado
            para que sea lo primero que se lee al llegar desde el sitio
            web, y se queda fija al hacer scroll -- un aviso que se pierde
            al bajar no sirve de nada. Solo aparece en sesiones de demo
            (ver AvisoDemo.js). */}
        <AvisoDemo />

        {/* Encabezado */}
        <NotionHeader
          title={title}
          breadcrumbs={breadcrumbs}
          onMenuClick={toggleDrawer}
          sidebarOpen={open}
        />

        {/* Espaciador para la barra de herramientas */}
        <Toolbar variant="dense" />

        {/* Aviso de prueba gratuita / vencimiento */}
        <TrialBanner />

        {/* Contenido principal con desplazamiento independiente */}
        {/* 7 ago 2026: padding lateral reducido en pantallas chicas -- 24px
            de cada lado (p:3) le come ancho útil a una pantalla de celular
            (~375-400px), empujando tablas y formularios a desbordar.
            28 ago 2026 (pedido de Augusto: "veo muchos espacios en
            blanco... scroll up and down tedioso"): el padding VERTICAL de
            arriba/abajo (py) se reduce un poco respecto al horizontal
            (px) -- antes eran iguales (p:3 = 24px los 4 lados en
            escritorio), y ese aire de sobra arriba/abajo de cada página
            era justo lo que obligaba a hacer scroll de más para llegar al
            contenido real. El pb del Container también baja de 4 (32px) a
            2 (16px) por el mismo motivo -- ya no hace falta tanto colchón
            al final si el scroll interno del área de contenido ya deja
            ver claramente dónde termina. */}
        <Box
          sx={{
            flexGrow: 1,
            px: { xs: 1.5, sm: 3 },
            py: { xs: 1, sm: 2 },
            overflow: 'auto',
            backgroundColor: theme.palette.background.default,
          }}
        >
          <Container maxWidth="xl" disableGutters={isMobile} sx={{ pb: 2 }}>
            {isBlockedByTrial ? <TrialExpiredScreen /> : children || <Outlet />}
          </Container>
        </Box>

        {/* 17 sep 2026 (Augusto: "Copia el asistente de ayuda de Capital
            Optima"): botón flotante "?" disponible en TODA la app
            autenticada. Vive acá, en el layout, y no en cada pantalla,
            porque la ayuda tiene que estar donde sea que el cliente se
            trabe. No se muestra mientras la prueba está vencida porque
            esa pantalla ya tiene su propio camino (pagar). */}
        {!isBlockedByTrial && <HelpAssistant />}
      </Box>
    </Box>
    </OnboardingRefreshContext.Provider>
  );
};

export default NotionLayout;
